import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { StoreRealtimeService, StoreEvent } from '../../../core/services/store-realtime.service';
import { StoreContextService } from '../../../core/services/store-context.service';
import { ToastService } from '../../../core/services/toast.service';
import { KdsChimeService } from '../../kds/kds-chime.service';
import { isPosOperatorScreen } from '../shared/pos-operator-screen';

/**
 * SO-2 — the POS-wide alert when a scheduled order goes to the kitchen, fails to,
 * or misses its kitchen time (plan §4.5 "global handlers … chimes on release and
 * failure").
 *
 * <p>ROOT-level, started once from AppComponent — not in the POS shell, which is
 * mounted only on `/pos/terminal`: a cashier on the order or settle screen (sibling
 * routes) would otherwise never hear that a party's order failed to reach the
 * kitchen. The realtime socket is a root singleton, so this outlives every screen.</p>
 *
 * <p>Only on POS operator screens (`/pos/...`) and only for a store with the feature
 * on: the same app serves the KDS, kiosk, pickup and customer displays, and a guest
 * must never see "could not be sent to the kitchen".</p>
 */
@Injectable({ providedIn: 'root' })
export class ScheduledOrderAlertsService {
  private started = false;

  constructor(
    private readonly realtime: StoreRealtimeService,
    private readonly storeContext: StoreContextService,
    private readonly toast: ToastService,
    private readonly chime: KdsChimeService,
    private readonly router: Router,
    private readonly zone: NgZone,
  ) {}

  start(): void {
    if (this.started) return;
    this.started = true;
    this.realtime.event$.subscribe(e => this.zone.run(() => this.handle(e)));
  }

  /** Public for tests. */
  handle(e: StoreEvent | null | undefined): void {
    if (!e?.type?.startsWith('scheduled.order.')) return;
    if (!isPosOperatorScreen(this.router.url)) return;
    if (this.storeContext.activeStore?.scheduledOrdersEnabled !== true) return;

    switch (e.type) {
      case 'scheduled.order.released':
        this.chime.playScheduledReleased();
        this.toast.info('pos.scheduledOrders.alerts.released', 5000);
        break;
      case 'scheduled.order.release-failed':
        this.chime.playScheduledProblem();
        this.toast.error('pos.scheduledOrders.alerts.releaseFailed', 10000);
        break;
      case 'scheduled.order.missed':
        this.chime.playScheduledProblem();
        this.toast.error('pos.scheduledOrders.alerts.missed', 10000);
        break;
    }
  }
}
